import React, { useContext, useState } from "react";
import { FormContext } from "../form_buttons";
import { Box, Grid, Rating, Typography } from "@mui/material";
import { TypeConf } from "../../types";
import { FormField, Identifiable } from "../../form_model";

interface FormRatingProp<T> {
    field: string;
    label: string;
    disabled?: boolean;
    onChange?: (value: any) => void;
    max?: number;
    type?: TypeConf<T>;
    typeValues?: T[];
}

function modelToValue<T extends Identifiable>(
    modelField: FormField,
    type: TypeConf<T> | undefined,
    typeValues: T[],
): number | null {
    return itemOrNumberToNumber(modelField.value() as T | number | null | undefined, type, typeValues);
}

function valueToModel<T extends Identifiable>(
    value: number | null,
    type: TypeConf<T> | undefined,
    typeValues: T[],
): T | number | undefined | null {
    if (type && value && typeValues) {
        return typeValues[value - 1];
    }
    return value;
}

function locateType<T extends Identifiable>(value: T, typeValues: T[]): [T, number] | undefined {
    let idx = -1;
    let elem: T | undefined;
    for (let i = 0; i < typeValues.length; i++) {
        elem = typeValues[i];
        if (elem && elem.id === value.id) {
            idx = i;
            break;
        }
    }
    return idx >= 0 && elem ? [elem, idx] : undefined;
}

function itemOrNumberToNumber<T extends Identifiable>(
    value: T | null | undefined | number,
    type: TypeConf<T> | undefined,
    typeValues: T[],
): number | null {
    if (!value) return null;
    if (type && (value as T).id) {
        const tuple = locateType(value as T, typeValues);
        return tuple ? tuple[1] + 1 : null;
    }
    if (typeof value === "number") return value;
    throw new Error("Rating value is neither a typed item nor a number");
}

function FormRating<T extends Identifiable>({
    field,
    label,
    disabled,
    onChange,
    max,
    type,
    typeValues = [],
}: FormRatingProp<T>) {
    const formContext = useContext(FormContext);
    const form = formContext.form;
    const modelField = form.field(field);

    if (type && typeValues.length === 0) {
        throw new Error("FormRating: typeValues is required when type is set");
    }

    const [value, setValue] = useState<number | null>(modelToValue(modelField, type, typeValues));

    form.register(
        field,
        () => valueToModel(value, type, typeValues),
        incoming => setValue(itemOrNumberToNumber(incoming, type, typeValues)),
    );

    const printLabel = (): string => {
        if (value && type) {
            const elem = typeValues[value - 1];
            if (elem) return type.labeler(elem);
        }
        return "";
    };

    const handleChange = (_: React.SyntheticEvent, newValue: number | null) => {
        setValue(newValue);
        const modelValue = valueToModel(newValue, type, typeValues);
        form.notifyFieldChange(field, modelValue);
        onChange?.(newValue);
    };

    return (
        <Box my={3} className={["form-input", "form-input-" + field].join(" ")}>
            <Typography component="legend">{label}</Typography>
            <Grid container spacing={3}>
                <Grid item xs={2}>
                    <Rating
                        max={type ? typeValues.length : max}
                        disabled={disabled}
                        value={value}
                        id={field}
                        onChange={handleChange}
                    />
                </Grid>
                {type && value && (
                    <Grid item>
                        <Box sx={{ fontSize: "0.8em" }} color="gray">
                            {printLabel()}
                        </Box>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
}

export default FormRating;
